/**
 * PORTAL CLIENTE — SUS PROPIAS OT (PORTAL-DEBT-01, DEBT-ZERO-01)
 *
 * Pantalla DISTINTA de `PaginaOtsGlobal`/`PaginaOt` (gestión interna), no
 * la misma reutilizada: sin selector de cliente (CLIENTE es uno solo), sin
 * ninguna acción de gestión. Sólo lectura: código, fechas, estado, líneas, y
 * las secciones de Evidencias/Documentos que CLIENTE ya podía ver desde
 * 4.4-C/4.4-F (mismos endpoints, mismo permiso, sin cambios aquí).
 *
 * PORTAL CLIENTE v2
 *   · Listado: cada tarjeta dice qué limpieza es sin abrirla (primera tarea y cuántas más, sala, fecha, avance y
 *     avisos), con filtros de centro y de situación — los mismos grupos que las cifras de Inicio, que enlazan aquí.
 *   · Detalle: por tarea, quién la realizó y cuándo, quién la verificó y cuándo — los hechos reales (0098), nunca
 *     la persona asignada. Pensado como evidencia: se lee de un vistazo y se imprime bien.
 *
 * PC-v2.1-B — el cliente verifica, una a una, las tareas CONFIRMADAS que el servidor marca `verificable`
 * (`POST /api/portal/ot-lineas/:id/verificar`, permiso `ot_lineas:verificar_propias`). La pantalla sólo esconde el
 * botón donde no toca: quien decide es el servidor (0099). La respuesta trae la OT ya recalculada y se pinta sin
 * recargar. Sin verificación masiva.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

import {
  api,
  FalloApi,
  type GrupoOtPortal,
  type NcPublicaFila,
  type OtPortal,
  type OtPortalDetalle,
  type OtPortalLinea,
  type PaginaOtPortal,
} from '../api.ts';
import { useSesion } from '../contexto.tsx';
import { FormularioNcCliente, TarjetaNcPublica } from './portal-calidad.tsx';
import { useCentrosPortal } from './portal-inicio.tsx';
import {
  Cargando,
  ConfirmDialog,
  Error as ErrorCaja,
  EstadoLinea,
  EstadoOt,
  etiquetaEstadoOt,
  fecha,
  FiltrosResponsive,
  useDatos,
  Vacio,
} from '../componentes/base.tsx';
import { DocumentosAplicables } from '../componentes/documentos-aplicables.tsx';
import { Evidencias } from '../componentes/evidencias.tsx';
import { Shell } from '../componentes/shell.tsx';
import { useListaUrl } from '../listaUrl.ts';

const ESTADOS_PORTAL = [
  'PLANIFICADA', 'ASIGNADA', 'EN_CURSO', 'COMPLETADA', 'VERIFICADA',
  'CERRADA', 'REABIERTA', 'VALIDADA', 'RECHAZADA', 'NO_REALIZADA', 'ANULADA',
] as const;

/** Mismo orden y mismas palabras que las cifras de Inicio. */
export const GRUPOS_PORTAL: readonly { readonly clave: GrupoOtPortal; readonly texto: string }[] = [
  { clave: 'REALIZADAS', texto: 'Realizadas' },
  { clave: 'VERIFICADAS', texto: 'Verificadas' },
  { clave: 'PENDIENTES', texto: 'Pendientes' },
  { clave: 'VENCIDAS', texto: 'Fuera de plazo' },
  { clave: 'CON_INCIDENCIA', texto: 'Con incidencia' },
  { clave: 'NO_REALIZADAS', texto: 'No realizadas' },
];

const DEFAULTS_MIS_OTS = { estado: '', fechaDesde: '', fechaHasta: '', idCentro: '', grupo: '', pagina: '1' } as const;

export function PaginaMisOts(): ReactNode {
  const lista = useListaUrl(DEFAULTS_MIS_OTS, 'pagina');
  const estado = lista.valor('estado');
  const fechaDesde = lista.valor('fechaDesde');
  const fechaHasta = lista.valor('fechaHasta');
  const idCentro = lista.valor('idCentro');
  const grupo = lista.valor('grupo');
  const pagina = Number(lista.valor('pagina'));
  const centros = useCentrosPortal();

  const consulta = new URLSearchParams();
  if (estado !== '') consulta.set('estado', estado);
  if (fechaDesde !== '') consulta.set('fechaDesde', fechaDesde);
  if (fechaHasta !== '') consulta.set('fechaHasta', fechaHasta);
  if (idCentro !== '') consulta.set('idCentro', idCentro);
  if (grupo !== '') consulta.set('grupo', grupo);
  consulta.set('pagina', String(pagina));
  consulta.set('porPagina', '20');

  const datos = useDatos(
    () => api.get<PaginaOtPortal>(`/api/portal/ots?${consulta.toString()}`),
    [consulta.toString()],
  );
  const totalPaginas = Math.max(1, Math.ceil((datos.datos?.total ?? 0) / 20));
  const textoGrupo = GRUPOS_PORTAL.find((g) => g.clave === grupo)?.texto;

  return (
    <Shell titulo="Mis órdenes de trabajo">
      <div className="panel">
        <div className="panel-cabecera">
          <h2>Órdenes de trabajo{textoGrupo !== undefined ? ` · ${textoGrupo}` : ''}</h2>
          <span className="suave pequena">{datos.datos?.total ?? 0} en total</span>
        </div>

        <FiltrosResponsive
          contador={[estado !== '', fechaDesde !== '', fechaHasta !== '', idCentro !== '', grupo !== ''].filter(Boolean).length}
        >
          {centros.length > 1 && (
            <div className="campo">
              <label htmlFor="mis-ots-centro">Centro</label>
              <select id="mis-ots-centro" value={idCentro} onChange={(e) => lista.fijar('idCentro', e.target.value)}>
                <option value="">Todos tus centros</option>
                {centros.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </div>
          )}
          <div className="campo">
            <label htmlFor="mis-ots-grupo">Situación</label>
            <select id="mis-ots-grupo" value={grupo} onChange={(e) => lista.fijar('grupo', e.target.value)}>
              <option value="">Todas</option>
              {GRUPOS_PORTAL.map((g) => <option key={g.clave} value={g.clave}>{g.texto}</option>)}
            </select>
          </div>
          <div className="campo">
            <label htmlFor="mis-ots-estado">Estado</label>
            <select id="mis-ots-estado" value={estado} onChange={(e) => lista.fijar('estado', e.target.value)}>
              <option value="">Todos</option>
              {ESTADOS_PORTAL.map((es) => (
                <option key={es} value={es}>
                  {etiquetaEstadoOt(es)}
                </option>
              ))}
            </select>
          </div>
          <div className="campo">
            <label htmlFor="mis-ots-desde">Desde</label>
            <input
              id="mis-ots-desde" type="date" value={fechaDesde}
              onChange={(e) => lista.fijar('fechaDesde', e.target.value)}
            />
          </div>
          <div className="campo">
            <label htmlFor="mis-ots-hasta">Hasta</label>
            <input
              id="mis-ots-hasta" type="date" value={fechaHasta}
              onChange={(e) => lista.fijar('fechaHasta', e.target.value)}
            />
          </div>
          {!lista.esPorDefecto && (
            <button className="pequeno" onClick={lista.limpiar}>Limpiar filtros</button>
          )}
        </FiltrosResponsive>

        <div className="panel-cuerpo sin-relleno">
          {datos.cargando && datos.datos === null ? (
            <Cargando />
          ) : datos.error !== null ? (
            <ErrorCaja error={datos.error} reintentar={datos.recargar} />
          ) : (datos.datos?.filas.length ?? 0) === 0 ? (
            <Vacio titulo={lista.esPorDefecto ? 'No hay órdenes de trabajo' : 'Ninguna orden coincide con estos filtros'} />
          ) : (
            //  Siempre en tarjetas, en cualquier ancho — a diferencia de
            //  `.lista-ots-movil` (que sólo se ve por debajo de 600px,
            //  alternando con una tabla que aquí no existe), este portal
            //  no tiene una segunda representación de escritorio: se
            //  fuerza visible con estilo inline, no se pelea con la media
            //  query de esa clase.
            <ul className="portal-ots" style={{ display: 'flex', flexDirection: 'column', gap: 10, margin: 0, padding: 0, listStyle: 'none' }}>
              {datos.datos!.filas.map((o) => <TarjetaMiOt key={o.id} ot={o} />)}
            </ul>
          )}
        </div>

        {totalPaginas > 1 && (
          <div className="panel-pie fila" style={{ justifyContent: 'center', gap: 8 }}>
            <button className="pequeno" disabled={pagina <= 1} onClick={() => lista.fijar('pagina', String(pagina - 1))}>
              Anterior
            </button>
            <span className="pequena suave">Página {pagina} de {totalPaginas}</span>
            <button className="pequeno" disabled={pagina >= totalPaginas} onClick={() => lista.fijar('pagina', String(pagina + 1))}>
              Siguiente
            </button>
          </div>
        )}
      </div>
    </Shell>
  );
}

/** «Limpieza de quirófano · y 2 tareas más» — la primera tarea identifica la OT. */
export function trabajoOtPortal(tareas: readonly string[]): { titulo: string; mas: number } | null {
  return tareas.length === 0 ? null : { titulo: tareas[0]!, mas: tareas.length - 1 };
}

export function textoAvancePortal(a: { lineasTotales: number; lineasEjecutadas: number; lineasVerificadas?: number }): string {
  if (a.lineasTotales === 0) return 'Sin tareas';
  const base = `${String(a.lineasEjecutadas)} de ${String(a.lineasTotales)} tareas hechas`;
  return a.lineasVerificadas === undefined ? base : `${base} · ${String(a.lineasVerificadas)} verificadas`;
}

function sala(ot: { salaCodigo: string; salaDescripcion: string }): ReactNode {
  const nombre = ot.salaDescripcion !== '' && ot.salaDescripcion !== ot.salaCodigo ? ot.salaDescripcion : null;
  return <>{nombre !== null && <>{nombre} · </>}<span className="cod">{ot.salaCodigo}</span></>;
}

function AvisosOt({ ot }: { ot: OtPortal }): ReactNode {
  const avisos: { clave: string; texto: string; tono: string }[] = [];
  if (ot.vencida) avisos.push({ clave: 'v', texto: 'Fuera de plazo', tono: 'aviso' });
  if (ot.avance.lineasNoOk > 0) avisos.push({ clave: 'n', texto: ot.avance.lineasNoOk === 1 ? '1 tarea No OK' : `${String(ot.avance.lineasNoOk)} tareas No OK`, tono: 'error' });
  else if (ot.conIncidencia) avisos.push({ clave: 'i', texto: 'Con incidencia', tono: 'aviso' });
  if (avisos.length === 0) return null;
  return (
    <div className="tarjeta-ot-secundario" aria-label="Avisos">
      {avisos.map((a) => <span key={a.clave} className={`etiqueta ${a.tono}`}>{a.texto}</span>)}
    </div>
  );
}

function TarjetaMiOt({ ot }: { ot: OtPortal }): ReactNode {
  const trabajo = trabajoOtPortal(ot.tareas);
  return (
    <li className="tarjeta-ot">
      <div className="tarjeta-ot-cabecera">
        <Link to={`/mis-ots/${ot.id}`} className="tarjeta-ot-codigo" aria-label={`Abrir OT ${ot.codigo}`}
          style={{ minHeight: 44, display: 'inline-flex', alignItems: 'center' }}>
          {ot.codigo}
        </Link>
        <EstadoOt estado={ot.estado} />
      </div>
      {trabajo !== null && (
        <div className="tarjeta-ot-trabajo">
          <strong>{trabajo.titulo}</strong>
          {trabajo.mas > 0 && <span className="suave pequena"> · y {trabajo.mas === 1 ? '1 tarea más' : `${String(trabajo.mas)} tareas más`}</span>}
        </div>
      )}
      <div className="tarjeta-ot-sala">{sala(ot)}</div>
      <div className="tarjeta-ot-datos">
        <div className="tarjeta-ot-fecha">{fecha(ot.fechaPlan)}</div>
        <div>{textoAvancePortal(ot.avance)}</div>
        <div className="suave pequena">
          {ot.centroDescripcion} <span className="cod">{ot.centroCodigo}</span>
        </div>
      </div>
      <AvisosOt ot={ot} />
    </li>
  );
}

export function PaginaMiOt(): ReactNode {
  const { idOt } = useParams<{ idOt: string }>();
  const volver = (useLocation().state as { volver?: { a: string; texto: string } } | null)?.volver ?? null;
  const datos = useDatos(() => api.get<OtPortalDetalle>(`/api/portal/ots/${idOt}`), [idOt]);
  //  PC-v2.1-B: la OT que devuelve una verificación sustituye a la cargada, sin recargar la página.
  const [actualizada, setActualizada] = useState<OtPortalDetalle | null>(null);
  const [avisoVerificacion, setAvisoVerificacion] = useState<{ ok: true } | { error: unknown } | null>(null);
  useEffect(() => { setActualizada(null); }, [datos.datos]);
  useEffect(() => { setAvisoVerificacion(null); }, [idOt]);

  if (datos.cargando && datos.datos === null) return <Shell titulo="Orden de trabajo"><Cargando /></Shell>;
  if (datos.error !== null) {
    return <Shell titulo="Orden de trabajo"><ErrorCaja error={datos.error} reintentar={datos.recargar} /></Shell>;
  }
  const ot = actualizada !== null && actualizada.id === datos.datos?.id ? actualizada : datos.datos;
  if (ot === null) return <Shell titulo="Orden de trabajo"><Vacio titulo="No encontrada" /></Shell>;
  const trabajo = trabajoOtPortal(ot.tareas);

  return (
    <Shell
      titulo={ot.codigo}
      migas={[volver ?? { texto: 'Mis órdenes de trabajo', a: '/mis-ots' }, { texto: ot.codigo }]}
      subtitulo={<EstadoOt estado={ot.estado} />}
    >
      <div className="panel">
        <div className="panel-cabecera"><h2>Datos</h2></div>
        <div className="panel-cuerpo">
          {trabajo !== null && (
            <p className="tarjeta-ot-trabajo" style={{ marginTop: 0 }}>
              <strong>{trabajo.titulo}</strong>
              {trabajo.mas > 0 && <span className="suave pequena"> · y {trabajo.mas === 1 ? '1 tarea más' : `${String(trabajo.mas)} tareas más`}</span>}
            </p>
          )}
          <div className="datos">
            <div><div className="etiqueta-campo">Centro</div><div>{ot.centroDescripcion} <span className="cod suave">{ot.centroCodigo}</span></div></div>
            <div><div className="etiqueta-campo">Sala</div><div>{sala(ot)}</div></div>
            <div><div className="etiqueta-campo">Fecha</div><div>{fecha(ot.fechaPlan)}</div></div>
            <div><div className="etiqueta-campo">Fecha límite</div><div>{fecha(ot.fechaLimite)}</div></div>
            <div><div className="etiqueta-campo">Avance</div><div>{textoAvancePortal(ot.avance)}</div></div>
          </div>
          <AvisosOt ot={ot} />
        </div>
      </div>

      <div className="panel">
        <div className="panel-cabecera">
          <h2>Tareas</h2>
          <span className="suave pequena">Quién la realizó y quién la verificó</span>
        </div>
        <div className="panel-cuerpo">
          {avisoVerificacion !== null && ('ok' in avisoVerificacion ? (
            <div className="nota exito" role="status">Tarea verificada. Ha quedado registrada a tu nombre.</div>
          ) : <ErrorCaja error={avisoVerificacion.error} />)}
          {ot.lineas.length === 0 ? (
            <Vacio titulo="Esta orden no tiene tareas" />
          ) : (
            <ol className="portal-tareas" aria-label="Tareas de la orden">
              {ot.lineas.map((l) => (
                <TareaMiOt
                  key={l.id}
                  idOt={ot.id}
                  linea={l}
                  alVerificar={(o) => { setActualizada(o); setAvisoVerificacion({ ok: true }); }}
                  alRechazo={(e) => { setAvisoVerificacion({ error: e }); datos.recargar(); }}
                />
              ))}
            </ol>
          )}
        </div>
      </div>

      <Evidencias ambito="OT" ruta={`/api/ots/${ot.id}/evidencias`} titulo="Evidencias de la OT" />
      <DocumentosAplicables ruta={`/api/ots/${ot.id}/documentos`} titulo="Documentación aplicable a la OT" />
      <NoConformidadesDeMiOt ot={ot} />
    </Shell>
  );
}

/**
 * FASE 7.3 / F11.3 — el cliente abre una NC desde su OT con EL MISMO formulario
 * (y la misma vía del servidor) que desde Calidad: OT fija, líneas opcionales,
 * centro inferido, sin gravedad. Debajo, las NC visibles de esta OT.
 */
function NoConformidadesDeMiOt({ ot }: { ot: OtPortalDetalle }): ReactNode {
  const { puede } = useSesion();
  const lista = useDatos(() => api.get<{ filas: NcPublicaFila[] }>(`/api/portal/calidad/no-conformidades?idOt=${ot.id}`), [ot.id]);
  const [abriendo, setAbriendo] = useState(false);
  const [mensaje, setMensaje] = useState<{ idNc: string; codigo: string } | null>(null);

  return (
    <div className="panel">
      <div className="panel-cabecera">
        <h2>No conformidades</h2>
        {puede('nc:crear') && !abriendo && (
          <button type="button" className="pequeno" onClick={() => { setAbriendo(true); setMensaje(null); }}>
            Abrir no conformidad
          </button>
        )}
      </div>
      <div className="panel-cuerpo">
        {mensaje !== null && (
          <div className="nota exito" role="status">
            No conformidad {mensaje.codigo} abierta. El equipo de servicio ya la ve.{' '}
            <Link to={`/portal/calidad/no-conformidades/${mensaje.idNc}`}>Ver y seguirla en Calidad</Link>
          </div>
        )}
        {abriendo && (
          <FormularioNcCliente
            ot={{ id: ot.id, codigo: ot.codigo, centro: ot.centroDescripcion, lineas: ot.lineas.map((l) => ({ id: l.id, ruta: l.ubicacionRuta })) }}
            cancelar={() => setAbriendo(false)}
            alCrear={(idNc, codigo) => { setAbriendo(false); setMensaje({ idNc, codigo }); lista.recargar(); }}
          />
        )}
        {lista.error !== null && <ErrorCaja error={lista.error} reintentar={lista.recargar} />}
        {lista.datos !== null &&
          (lista.datos.filas.length === 0 ? (
            <p className="pequena tenue">No hay no conformidades visibles para esta orden.</p>
          ) : (
            <ul className="lista-nc">{lista.datos.filas.map((n) => <TarjetaNcPublica key={n.id} n={n} />)}</ul>
          ))}
      </div>
    </div>
  );
}

/**
 * Lo que se dice de cada tarea, en palabras de cliente. «Pendiente» sólo donde hay algo que esperar: una tarea
 * realizada y aún sin verificar. Nunca se usa la persona asignada en lugar de quien la realizó.
 */
export function trazaTarea(l: OtPortalLinea): {
  realizadaPor: string; realizadaEn: string; verificadaPor: string; verificadaEn: string; nota: string | null;
} {
  const realizada = l.realizadaPor !== null;
  const verificadaPor = l.verificadaPor !== null ? l.verificadaPor : l.estado === 'CONFIRMADA' ? 'Pendiente' : '—';
  let nota: string | null = null;
  if (l.estado === 'NO_OK') nota = `Resultado no aceptado (No OK)${l.noOkEn !== null ? ` el ${fecha(l.noOkEn)}` : ''}. Se tratará como incidencia de calidad.`;
  else if (l.estado === 'NO_APLICA') nota = 'Esta tarea no aplicaba en esta ocasión.';
  return {
    realizadaPor: realizada ? l.realizadaPor! : '—',
    realizadaEn: l.realizadaEn !== null ? fecha(l.realizadaEn) : '—',
    verificadaPor,
    verificadaEn: l.verificadaEn !== null ? fecha(l.verificadaEn) : '—',
    nota,
  };
}

export const TEXTO_CONFIRMAR_VERIFICACION_CLIENTE =
  '¿Confirmas que esta limpieza se ha realizado correctamente? Esta verificación quedará registrada y no podrá deshacerse directamente.';

/**
 * PC-v2.1-B — «Verificar» de UNA tarea. El id de operación nace con el diálogo y se reutiliza en cada reintento
 * (doble clic, red caída): el servidor lo reconoce y no registra dos veces. Un 409 (ya verificada por otro, ya no
 * confirmada…) se explica y la OT se vuelve a leer, para que el botón desaparezca si ya no toca.
 */
function VerificarTareaCliente({ linea, alVerificar, alRechazo }: {
  linea: OtPortalLinea;
  alVerificar: (ot: OtPortalDetalle) => void;
  alRechazo: (error: unknown) => void;
}): ReactNode {
  const [idOperacion, setIdOperacion] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function verificar(): Promise<void> {
    if (idOperacion === null || enviando) return;
    setEnviando(true);
    setError(null);
    try {
      const r = await api.post<{ yaEstaba: boolean; ot: OtPortalDetalle }>(
        `/api/portal/ot-lineas/${linea.id}/verificar`,
        { idOperacionCliente: idOperacion },
      );
      setIdOperacion(null);
      alVerificar(r.ot);
    } catch (e) {
      //  404/409: la tarea ya no se puede verificar (otro la verificó, cambió de estado…). Se cierra el diálogo, se
      //  explica en la pantalla y se vuelve a leer la OT. Cualquier otro fallo deja el diálogo para reintentar.
      if (e instanceof FalloApi && (e.estado === 404 || e.estado === 409)) {
        setIdOperacion(null);
        alRechazo(e);
      } else {
        setError(e);
      }
    } finally {
      setEnviando(false);
    }
  }

  return (
    <>
      <button type="button" className="principal pequeno portal-tarea-verificar" onClick={() => { setError(null); setIdOperacion(crypto.randomUUID()); }}>
        Verificar
      </button>
      {idOperacion !== null && (
        <ConfirmDialog
          titulo="Verificar tarea"
          descripcion={(
            <>
              <p style={{ marginTop: 0 }}>{TEXTO_CONFIRMAR_VERIFICACION_CLIENTE}</p>
              <p className="pequena suave">{linea.descripcionTarea ?? linea.tipoDescripcion} · {linea.ubicacionRuta}</p>
              {error !== null && <ErrorCaja error={error} />}
            </>
          )}
          confirmar="Verificar"
          variante="principal"
          ocupado={enviando}
          alConfirmar={verificar}
          cerrar={() => { if (!enviando) setIdOperacion(null); }}
        />
      )}
    </>
  );
}

function TareaMiOt({ idOt, linea, alVerificar, alRechazo }: {
  idOt: string;
  linea: OtPortalLinea;
  alVerificar: (ot: OtPortalDetalle) => void;
  alRechazo: (error: unknown) => void;
}): ReactNode {
  const [abierta, setAbierta] = useState(false);
  const t = trazaTarea(linea);
  return (
    <li className={`portal-tarea estado-${linea.estado.toLowerCase()}`}>
      <div className="portal-tarea-cabecera">
        <div>
          <strong>{linea.descripcionTarea ?? linea.tipoDescripcion}</strong>
          <div className="pequena suave">{linea.ubicacionRuta}</div>
        </div>
        <EstadoLinea estado={linea.estado} />
      </div>
      <dl className="portal-tarea-traza">
        <div><dt>Realizada por</dt><dd>{t.realizadaPor}</dd></div>
        <div><dt>Fecha de realización</dt><dd>{t.realizadaEn}</dd></div>
        <div><dt>Verificada por</dt><dd className={t.verificadaPor === 'Pendiente' ? 'pendiente' : undefined}>{t.verificadaPor}</dd></div>
        <div><dt>Fecha de verificación</dt><dd>{t.verificadaEn}</dd></div>
      </dl>
      {t.nota !== null && <p className="pequena portal-tarea-nota">{t.nota}</p>}
      {linea.verificable && <VerificarTareaCliente linea={linea} alVerificar={alVerificar} alRechazo={alRechazo} />}
      <button type="button" className="enlace pequena" aria-expanded={abierta} onClick={() => setAbierta((a) => !a)}>
        {abierta ? 'Ocultar evidencias y documentos' : 'Ver evidencias y documentos'}
      </button>
      {abierta && (
        <div className="portal-tarea-anexos">
          <Evidencias ambito="LINEA" ruta={`/api/ots/${idOt}/lineas/${linea.id}/evidencias`} titulo="Evidencias de la tarea" />
          <DocumentosAplicables ruta={`/api/ots/${idOt}/lineas/${linea.id}/documentos`} titulo="Documentación aplicable a la tarea" />
        </div>
      )}
    </li>
  );
}
